import { MockService } from '../../../core/services/mock.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@environment';
import { Observable, map, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { Option } from 'src/app/base/models/option.model';
import { CustomerResponse, EndCustomerRow, InvOrgRow, ItemResponse, OuOrgResponse, RefPriceRequest, RefPriceResponse, SalesCostInqHeaderRequest, SalesCostInqHeaderResponse, SubAreaRow, EccnResponse,VendorSiteResponse, MajorCustomerAnalyRequest, MajorCustomerAnalyResponse, InternalTransferPriceEndCustomerRequest, InternalTransferPriceEndCustomerResponse, InternalTransferPriceInqRequest, InternalTransferPriceInqResponse } from '../models/common.model';

@Injectable({
  providedIn: 'root',
})
export class CommonService {

  // 測試範例為了模擬資料查詢時間，實際開發不會用到
  delay: number = 500;

  constructor(
    private readonly mockService: MockService,
    private readonly httpClient: HttpClient,
    private readonly translateService: TranslateService
  ) {}
  
  /**
   * 取得Item
   * 如果取到值了，之後都用這個值直接回傳，不用每次都重新查詢。
   *
   * @param brand 廠牌
   * @param keyword item 查詢關鍵字
   */
  getItems(keyword?: string, inventoryItemId?: number): Observable<Option[]> {
    let endpoint = `${environment.commonApiEndpoint}/item/itemAll`;
    endpoint = this.mockService.parseApiEndpoint(endpoint, '/api/v1/common/items.json');
    
    const httpParams = new HttpParams({
      fromObject: {
        // 該參數有傳才帶去後端
        ...(keyword ? { item: keyword } : {}),
        ...(inventoryItemId ? { inventoryItemId: String(inventoryItemId) } : {}),
        ...{ excludeInactive: "Y" },
      }
    });

    return this.httpClient.get<ItemResponse[]>(endpoint, { params: httpParams }).pipe(
      map(response => {
        return response.map(itemResponse => ({
          id: itemResponse.inventoryItemId,
          value: itemResponse.item,
          label: itemResponse.item
        }));
      })
    );
  }

  /**
   * 取得Item（包含 description）
   *
   * @param keyword item 查詢關鍵字
   */
  getItemsWithDesc(keyword?: string, inventoryItemId?: number): Observable<(Option & { description?: string; uom?: string })[]> {
    let endpoint = `${environment.commonApiEndpoint}/item/itemAll`;
    endpoint = this.mockService.parseApiEndpoint(endpoint, '/api/v1/common/items.json');

    const httpParams = new HttpParams({
      fromObject: {
        ...(keyword ? { item: keyword } : {}),
        ...(inventoryItemId ? { inventoryItemId: String(inventoryItemId) } : {}),
        ...{ excludeInactive: "Y" },
      }
    });

    return this.httpClient.get<ItemResponse[]>(endpoint, { params: httpParams }).pipe(
      map(response => {
        return response.map(itemResponse => ({
          id: itemResponse.inventoryItemId,
          value: itemResponse.item,
          label: itemResponse.item,
          description: itemResponse.description ?? '',
          uom: itemResponse.uom ?? '',
          brand: itemResponse.brand ?? ''
        }));
      })
    );
  }

  /**
   * 取得客戶清單（依客戶編號模糊查詢）
   *
   * @param keyword 客戶編號查詢關鍵字
   */
  getCustomers(keyword: string): Observable<Option[]> {
    const endpoint = `${environment.commonApiEndpoint}/customer/numLike/${encodeURIComponent(keyword)}?allowDisable=false`;
    const currentLang = this.translateService.currentLang ?? '';
    const isChinese = currentLang.startsWith('zh');
    return this.httpClient.get<CustomerResponse[]>(endpoint).pipe(
      map(response =>
        response.map(customer => ({
          id: String(customer.customerId),
          value: customer.customerNumber,
          label: `${customer.customerNumber} - ${isChinese ? customer.custNameC : customer.custNameE}`,
        }))
      )
    );
  }

  /**
   * 取得客戶清單（依客戶編號或名稱模糊查詢）
   *
   * @param keyword 客戶編號或名稱查詢關鍵字
   * @param queryAll 是否查詢全部（預設 false）
   */
  getCustomersByNumAndName(keyword: string, queryAll: boolean = false): Observable<Option[]> {
    const endpoint = `${environment.commonApiEndpoint}/customer/num-and-name-like`;
    const currentLang = this.translateService.currentLang ?? '';
    const isChinese = currentLang.startsWith('zh');
    const httpParams = new HttpParams({
      fromObject: {
        customer: keyword,
        queryAll: String(queryAll),
      }
    });
    return this.httpClient.get<CustomerResponse[]>(endpoint, { params: httpParams }).pipe(
      map(response =>
        response.map(customer => ({
          id: String(customer.customerId),
          value: customer.customerNumber,
          label: `${customer.customerNumber} - ${isChinese ? customer.custNameC : customer.custNameE}`,
        }))
      )
    );
  }

  getSubAreas(ouId: number, empNo: string, uuid: string = ''): Observable<SubAreaRow[]> {
    const endpoint = `${environment.apiEndpoint}/sales-cost-inq/querySubArea`;
    return this.httpClient.post<{ queryId: string; responseData: SubAreaRow[] }>(endpoint, { ouId, empNo, uuid }).pipe(
      map(res => res.responseData ?? [])
    );
  }

  getEndCustomers(ouId: number, brand: string, uuid: string = ''): Observable<EndCustomerRow[]> {
    const ouOrgEndpoint = `${environment.commonApiEndpoint}/ouorg/ouNum/${ouId}`;
    const queryEndpoint = `${environment.apiEndpoint}/sales-cost-inq/queryEndCustomer`;
    return this.httpClient.get<OuOrgResponse>(ouOrgEndpoint).pipe(
      switchMap(ouOrg => {
        const corpGroup = ouOrg.corpGroup;
        return this.httpClient.post<{ queryId: string; responseData: EndCustomerRow[] }>(queryEndpoint, { ouId, brand, corpGroup, uuid });
      }),
      map(res => res.responseData ?? [])
    );
  }

  getInvOrgs(ouId: number, uuid: string = ''): Observable<InvOrgRow[]> {
    const endpoint = `${environment.apiEndpoint}/sales-cost-inq/queryInvOrg`;
    return this.httpClient.post<{ queryId: string; responseData: InvOrgRow[] }>(endpoint, { ouId, uuid }).pipe(
      map(res => res.responseData ?? [])
    );
  }

  getSalesCostInqData(request: SalesCostInqHeaderRequest): Observable<SalesCostInqHeaderResponse> {
    const endpoint = `${environment.apiEndpoint}/sales-cost-inq/querySalesCostInq`;
    return this.httpClient.post<SalesCostInqHeaderResponse>(endpoint, request);
  }

  getRefPriceData(request: RefPriceRequest): Observable<RefPriceResponse> {
    const endpoint = `${environment.apiEndpoint}/ref-price/queryRefPrice`;
    return this.httpClient.post<RefPriceResponse>(endpoint, request);
  }

  getItemEccn(model: any): Observable<EccnResponse> {
    const endpoint = `${environment.apiEndpoint}/eccn/queryEccn`;
    return this.httpClient.post<EccnResponse>(endpoint, model);
  }

  getVendorSite(ouId: string, vendorId: string): Observable<VendorSiteResponse[]> {
    let endpoint = `${environment.commonApiEndpoint}/vendor-site/orgIdVendorId/${ouId}/${vendorId}`;
    if (environment.apiMock) {
      endpoint = `assets/mock/api/v1/common/vendor-site.json`;
}
    return this.httpClient.get<VendorSiteResponse[]>(endpoint);
  }

  getMajorCustomerAnalyData(request: MajorCustomerAnalyRequest): Observable<MajorCustomerAnalyResponse> {
    const endpoint = `${environment.apiEndpoint}/marjo-customer-analy/queryMajorCustomerAnaly`;
    return this.httpClient.post<MajorCustomerAnalyResponse>(endpoint, request);
  }

  getOuOrg(ouId: number): Observable<OuOrgResponse> {
    const endpoint = `${environment.commonApiEndpoint}/ouorg/ouNum/${ouId}`;
    return this.httpClient.get<OuOrgResponse>(endpoint);
  }

  getOuOrgs(keyword?: string): Observable<Option[]> {
    const endpoint = `${environment.commonApiEndpoint}/ouorg/ouorgs`;
    return this.httpClient.get<OuOrgResponse[]>(endpoint).pipe(
      map(response => {
        const options: Option[] = response.map(ou => ({
          id: String(ou.ouNum),
          value: String(ou.ouNum),
          label: `${ou.ouNum} - ${ou.ouName}`,
        }));
        return keyword
          ? options.filter(o => o.label.toLowerCase().includes(keyword.toLowerCase()))
          : options;
      })
    );
  }

  getOrganizations(invOu: number): Observable<InvOrgRow[]> {
    const endpoint = `${environment.commonApiEndpoint}/organization/get/${invOu}?includeAll=false`;
    return this.httpClient.get<InvOrgRow[]>(endpoint);
  }

  getItpEndCustomers(request: InternalTransferPriceEndCustomerRequest): Observable<InternalTransferPriceEndCustomerResponse> {
    const endpoint = `${environment.apiEndpoint}/internal-transfer-price-inq/queryEndCustomer`;
    return this.httpClient.post<InternalTransferPriceEndCustomerResponse>(endpoint, request);
  }

  queryInternalTransferPriceInq(request: InternalTransferPriceInqRequest): Observable<InternalTransferPriceInqResponse> {
    const endpoint = `${environment.apiEndpoint}/internal-transfer-price-inq/queryInternalTransferPriceInq`;
    return this.httpClient.post<InternalTransferPriceInqResponse>(endpoint, request);
  }
}
